/**
 * ActivitiesSection Component
 * Displays play activities and ideas for the current phase
 * Supports HTML content from TipTap editor
 */

import { Dices, Clock } from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import parse from 'html-react-parser';
import type { BabyDevelopmentActivity } from '../types';

interface ActivitiesSectionProps {
  activities: BabyDevelopmentActivity[];
  isLoading?: boolean;
}

// Helper to check if content is HTML
function isHtmlContent(content: string): boolean {
  return /<[a-z][\s\S]*>/i.test(content);
}

export function ActivitiesSection({ activities, isLoading }: ActivitiesSectionProps) {
  const { t } = useTranslation('babyDevelopment');
  if (isLoading) {
    return (
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Dices className="w-5 h-5 text-white" />
          <h3 className="text-white text-xl font-bold">{t('activities.title')}</h3>
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 bg-white/10 rounded-[15px] animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (activities.length === 0) {
    return null;
  }

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <Dices className="w-5 h-5 text-white" />
        <h3 className="text-white text-xl font-bold">{t('activities.title')}</h3>
      </div>

      <div className="space-y-3">
        {activities.map((activity) => (
          <div
            key={activity.id}
            className="bg-white/10 rounded-[15px] p-4"
          >
            <h4 className="text-white font-medium">{activity.title}</h4>
            {activity.duration && (
              <div className="flex items-center gap-1 text-white/50 text-xs mt-1">
                <Clock className="w-3 h-3" />
                {activity.duration}
              </div>
            )}
            {isHtmlContent(activity.description) ? (
              <div className="prose prose-sm prose-invert max-w-none mt-2 prose-p:text-white/60 prose-p:leading-relaxed prose-p:my-1">
                {parse(activity.description)}
              </div>
            ) : (
              <p className="text-white/60 text-sm mt-2 leading-relaxed">
                {activity.description}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
